import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { api, primeCsrf } from './api';
import type { UserData } from '@/types/api';

export type AuthUser = UserData;

export const ME_KEY = ['auth', 'me'] as const;

export function useMe() {
  return useQuery({
    queryKey: ME_KEY,
    queryFn: async (): Promise<AuthUser | null> => {
      try {
        const { data } = await api.get<UserData>('/v1/me');
        return data;
      } catch (e) {
        if (e instanceof AxiosError && e.response?.status === 401) {
          return null;
        }
        throw e;
      }
    },
    staleTime: 60_000,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (creds: { email: string; password: string }) => {
      await primeCsrf();
      const { data } = await api.post<{ two_factor: boolean }>('/login', creds);
      // Populate the /me cache BEFORE the mutation resolves so the post-login
      // navigate() doesn't bounce off a stale `null` cached from the pre-login
      // 401 — which was clearing the form and forcing a second sign-in attempt.
      if (!data.two_factor) {
        const me = await api.get<UserData>('/v1/me');
        qc.setQueryData(ME_KEY, me.data);
      } else {
        qc.removeQueries({ queryKey: ME_KEY });
      }
      return data;
    },
  });
}

export function useTwoFactorChallenge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { code?: string; recovery_code?: string }) => {
      await primeCsrf();
      await api.post('/two-factor-challenge', payload);
      // Same as useLogin — prime the /me cache before resolving so the post-
      // challenge navigate() doesn't bounce off a stale 401 cache.
      const me = await api.get<UserData>('/v1/me');
      qc.setQueryData(ME_KEY, me.data);
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await primeCsrf();
      await api.post('/logout');
    },
    onSuccess: () => qc.setQueryData(ME_KEY, null),
  });
}

export function useUploadAvatar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      await primeCsrf();
      const fd = new FormData();
      fd.append('avatar', file);
      await api.post('/v1/me/avatar', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ME_KEY }),
  });
}

export function useDeleteAvatar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await primeCsrf();
      await api.delete('/v1/me/avatar');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ME_KEY }),
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { name: string; email: string }) => {
      await primeCsrf();
      await api.put('/user/profile-information', payload);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ME_KEY }),
  });
}

export function useUpdatePreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { theme_preference?: 'light' | 'dark' | 'system' }) => {
      await primeCsrf();
      const { data } = await api.patch<UserData>('/v1/me/preferences', payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ME_KEY }),
  });
}

export function useUpdatePassword() {
  return useMutation({
    mutationFn: async (payload: {
      current_password: string;
      password: string;
      password_confirmation: string;
    }) => {
      await primeCsrf();
      await api.put('/user/password', payload);
    },
  });
}

/** Fortify's 2FA endpoints sit behind the password.confirm middleware. */
export function useConfirmPassword() {
  return useMutation({
    mutationFn: async (password: string) => {
      await primeCsrf();
      await api.post('/user/confirm-password', { password });
    },
  });
}

/** True if the session has confirmed its password within the timeout window. */
export async function isPasswordConfirmed(): Promise<boolean> {
  const { data } = await api.get<{ confirmed: boolean }>('/user/confirmed-password-status');
  return data.confirmed;
}

export function useEnableTwoFactor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await primeCsrf();
      await api.post('/user/two-factor-authentication');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ME_KEY }),
  });
}

export function useConfirmTwoFactor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { code: string }) => {
      await primeCsrf();
      await api.post('/user/confirmed-two-factor-authentication', payload);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ME_KEY }),
  });
}

export function useDisableTwoFactor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await primeCsrf();
      await api.delete('/user/two-factor-authentication');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ME_KEY }),
  });
}

export function useTwoFactorQrCode() {
  return useQuery({
    queryKey: ['auth', 'two-factor', 'qr'],
    queryFn: async (): Promise<string> => {
      const { data } = await api.get<{ svg: string }>('/user/two-factor-qr-code');
      return data.svg;
    },
    enabled: false,
    retry: false,
  });
}

export function useTwoFactorSecret() {
  return useQuery({
    queryKey: ['auth', 'two-factor', 'secret'],
    queryFn: async (): Promise<string> => {
      const { data } = await api.get<{ secretKey: string }>('/user/two-factor-secret-key');
      return data.secretKey;
    },
    enabled: false,
    retry: false,
  });
}

export function useTwoFactorRecoveryCodes() {
  return useQuery({
    queryKey: ['auth', 'two-factor', 'recovery'],
    queryFn: async (): Promise<string[]> => {
      const { data } = await api.get<string[]>('/user/two-factor-recovery-codes');
      return data;
    },
    enabled: false,
    retry: false,
  });
}

export function useRegenerateRecoveryCodes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await primeCsrf();
      await api.post('/user/two-factor-recovery-codes');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['auth', 'two-factor', 'recovery'] }),
  });
}
